import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, LayoutDashboard, ActivitySquare, Users, BarChart3 } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';
import AdminQuickActions from '../components/AdminQuickActions';
import AdminTradeManager from '../components/AdminTradeManager';
import AdminPlanManager from '../components/AdminPlanManager';
import { AdminCreditSettings } from '../components/CreditSystem';
import CreditAnalyticsDashboard from '../components/CreditAnalyticsDashboard';
import PartnerCampaignManager from '../components/PartnerCampaignManager';
import AdminActivityFeed from '../components/AdminActivityFeed';
import AdminUserActivity from '../components/AdminUserActivity';
import AdminBusinessIntelligence from '../components/AdminBusinessIntelligence';

const TABS = [
  { k: 'overview', label: 'Overview', icon: LayoutDashboard },
  { k: 'activity', label: 'Activity Feed', icon: ActivitySquare },
  { k: 'bi', label: 'Business Intelligence', icon: BarChart3 },
  { k: 'config', label: 'Config', icon: Users },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [openUserId, setOpenUserId] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (response.data.user_type !== 'admin') { navigate('/login'); return; }
        setUser(response.data);
      } catch { navigate('/login'); }
      finally { setLoading(false); }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); }
    finally { navigate('/login'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-orange-500" data-testid="dashboard-logo" />
            <h1 className="text-xl font-bold text-slate-900">OnsiteHand Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="user-name">Hi, {user?.name}</span>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto -mb-px" role="tablist" aria-label="Admin sections">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.k;
              return (
                <button
                  key={t.k}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => { setActiveTab(t.k); setOpenUserId(null); }}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    active ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`admin-tab-${t.k}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading uppercase tracking-tight" data-testid="dashboard-title">
            Admin Dashboard
          </h2>
          <p className="text-slate-600 text-sm">Monitor platform usage, trades and configuration</p>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6" data-testid="admin-tabpanel-overview">
            <AdminQuickActions />
            <CreditAnalyticsDashboard />
            <PartnerCampaignManager />
          </div>
        )}

        {activeTab === 'activity' && (
          <div data-testid="admin-tabpanel-activity">
            {openUserId
              ? <AdminUserActivity userId={openUserId} onBack={() => setOpenUserId(null)} />
              : <AdminActivityFeed onOpenUser={(uid) => setOpenUserId(uid)} />}
          </div>
        )}

        {activeTab === 'bi' && (
          <div data-testid="admin-tabpanel-bi">
            <AdminBusinessIntelligence />
          </div>
        )}

        {activeTab === 'config' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-tabpanel-config">
            <div><AdminTradeManager /></div>
            <div><AdminCreditSettings /></div>
            <div className="lg:col-span-2"><AdminPlanManager /></div>
          </div>
        )}
      </div>
    </div>
  );
}
